/**
 * 設定コマンド
 */

import type { ArgumentsCamelCase, Argv } from 'yargs';
import { loadConfig, saveConfig } from '../lib/config-store.js';
import type { HelpOption, HelpPositional } from '../lib/help-schema.js';

interface ConfigShowArgs {}

interface ConfigSetArgs {
  key: string;
  value: string;
}

export function command(): string {
  return 'config <subcommand>';
}

export function describe(): string {
  return '設定の表示・更新';
}

/** --help 用の positionals（サブコマンド show | set と set 時の key, value） */
export const helpPositionals: HelpPositional[] = [
  { name: 'subcommand', type: 'string', required: true, description: 'show | set' },
  { name: 'key', type: 'string', required: false, description: '設定キー (baseUrl, tenantId, accessToken)（set 時）' },
  { name: 'value', type: 'string', required: false, description: '設定値（set 時）' },
];

/** --help 用のオプション一覧（共通 help JSON 用） */
export const helpOptions: HelpOption[] = [];

export function builder(yargs: Argv): Argv {
  return yargs
    .command(
      'show',
      '現在の設定を表示',
      (y) => y,
      async (argv) => {
        await showHandler(argv);
      }
    )
    .command(
      'set <key> <value>',
      '設定を更新',
      (y) =>
        y
          .positional('key', {
            type: 'string',
            description: '設定キー (baseUrl, tenantId, accessToken)',
          })
          .positional('value', {
            type: 'string',
            description: '設定値',
          }),
      async (argv) => {
        await setHandler(argv as ArgumentsCamelCase<ConfigSetArgs>);
      }
    )
    .demandCommand(1, '利用可能なサブコマンド: show, set');
}

export async function handler(argv: ArgumentsCamelCase): Promise<void> {
  // サブコマンドで処理されるため、ここには来ない
}

async function showHandler(argv: ArgumentsCamelCase<ConfigShowArgs>): Promise<void> {
  try {
    const config = loadConfig();
    if (!config) {
      console.log('設定ファイルが見つかりません。`acomo login` を実行してください。');
      return;
    }

    // トークンをマスク
    const masked = {
      ...config,
      accessToken: config.accessToken
        ? `${config.accessToken.slice(0, 10)}...`
        : '',
    };

    console.log(JSON.stringify(masked, null, 2));
    if (!config.baseUrl || config.baseUrl.trim() === '') {
      console.error(
        'baseUrl が未設定です。API 呼び出しが失敗する可能性があります。`acomo login` で設定するか、環境変数 ACOMO_BASE_URL を設定してください。',
      );
    }
  } catch (error: any) {
    console.error('設定の読み込みに失敗しました:', error.message);
    process.exit(1);
  }
}

async function setHandler(argv: ArgumentsCamelCase<ConfigSetArgs>): Promise<void> {
  try {
    const config = loadConfig();
    if (!config) {
      console.error('設定ファイルが見つかりません。`acomo login` を実行してください。');
      process.exit(1);
    }

    const { key, value } = argv;

    // 許可されたキーのみ更新
    const allowedKeys = ['baseUrl', 'tenantId', 'accessToken'];
    if (!allowedKeys.includes(key)) {
      console.error(`無効なキーです: ${key}`);
      console.error(`利用可能なキー: ${allowedKeys.join(', ')}`);
      process.exit(1);
    }

    (config as Record<string, string>)[key] = value;
    saveConfig(config);

    console.log(`設定を更新しました: ${key} = ${value}`);
  } catch (error: any) {
    console.error('設定の更新に失敗しました:', error.message);
    process.exit(1);
  }
}
